import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import PreLoader from './extra/PreLoader';

const money = (n) => `₦${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Matches the status badges the admin dashboard shows (resources/views/company/product_ecommerce).
const STATUS = {
  1: { label: 'Completed', className: 'bg-success' },
  2: { label: 'Awaiting Delivery', className: 'bg-warning' },
  3: { label: 'Cancelled', className: 'bg-secondary' },
  4: { label: 'Awaiting BNPL Approval', className: 'bg-info' },
  5: { label: 'BNPL Rejected', className: 'bg-danger' },
};
const CANCELLABLE = [2, 4];

const MyOrders = () => {
  const { http, user, logout } = AuthenticateService();

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'pending'
  const [orders, setOrders] = useState([]);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    document.title = 'My Orders';
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const load = async (which) => {
    setLoading(true);
    try {
      const url = which === 'pending' ? '/ecommerce/view-pending-order' : '/ecommerce/view-all-order';
      const res = await http.post(url, {});
      if (res.data.status === true) {
        setOrders(res.data.message || []);
      } else {
        toast.error('Could not load your orders');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        logout();
      } else {
        toast.error('Could not load your orders, please try again');
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (order) => {
    const confirm = await Swal.fire({
      title: 'Cancel this order?',
      text: `Order #${order.orderID}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No',
    });
    if (!confirm.isConfirmed) return;

    setCancellingId(order.id);
    try {
      const res = await http.post('/ecommerce/cancel-order', { order_id: order.id });
      if (res.data.status === true) {
        toast.success(res.data.message || 'Order cancelled');
        load(filter);
      } else {
        toast.error(res.data.message || 'Could not cancel this order');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not cancel this order');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <>
      {loading && orders.length === 0 ? (
        <PreLoader />
      ) : (
        <>
          <Sidebar />
          <Header user={user} />

          <div className="content">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
              <h4 className="fs-1 mb-2">My Orders</h4>
              <Link to="/store" className="btn btn-outline-primary mb-2">Continue Shopping</Link>
            </div>

            <div className="btn-group mb-4" role="group">
              <button type="button" className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('all')}>
                All Orders
              </button>
              <button type="button" className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter('pending')}>
                Pending
              </button>
            </div>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : orders.length === 0 ? (
              <div className="text-center py-5">
                <FontAwesomeIcon icon={faBoxOpen} size="3x" className="text-500 mb-3" />
                <p className="text-700">No orders here yet.</p>
              </div>
            ) : (
              <div className="card">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Total</th>
                        <th>Payment</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        const status = STATUS[order.status_id] || { label: 'Unknown', className: 'bg-secondary' };
                        return (
                          <tr key={order.id}>
                            <td>
                              <div className="fw-semi-bold">#{order.orderID}</div>
                              <div className="text-500 fs--1">{new Date(order.created_at).toLocaleString()}</div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                {order.product?.cover_img && (
                                  <img
                                    src={order.product.cover_img}
                                    alt={order.product?.name}
                                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = '/logo.png'; }}
                                  />
                                )}
                                <span>{order.product?.name || 'Product'}</span>
                              </div>
                              {order.DeliveryDetails?.address && (
                                <div className="text-500 fs--1 mt-1">Deliver to: {order.DeliveryDetails.address}</div>
                              )}
                            </td>
                            <td>{order.qrty}</td>
                            <td>{money(order.total_amount)}</td>
                            <td className="text-capitalize">{(order.payment_method || 'wallet').replace('_', ' ')}</td>
                            <td>
                              <span className={`badge ${status.className}`}>{status.label}</span>
                            </td>
                            <td>
                              {CANCELLABLE.includes(order.status_id) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={cancellingId === order.id}
                                  onClick={() => cancelOrder(order)}
                                >
                                  {cancellingId === order.id ? <Spinner animation="border" size="sm" /> : 'Cancel'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Footer />
          </div>

          <ToastContainer />
        </>
      )}
    </>
  );
};

export default MyOrders;
